import { AssortmentDetailSearchParams } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/utility/validationSchema';
import { AssortmentSearchParams } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/list/utility/validationSchema';
import { CyoDesignerSearchParams } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/designer/page';
import { ProductDetailSearchParams } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/utility/validationSchema';
import { MarketplaceSearchParams as MPSearchParams } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/utility/validationSchema';
import { GetPathNameArgs } from '@/i18n/navigation';
import { Locale, getCountry, getLanguage } from '@/utility/locale';
import { UnknownSearchParams } from '@/utility/unknownSearchParams';
import { slugifySearchTerm } from './slugifySearchTerm';
import { formatCharactersForUrlPathname } from './urlPathnameSanitizer';

// Transform a string to a URL-friendly format
export const sanitizePathname = (
  value: string,
  space: '+' | '-' = '-',
  equalLength: boolean = false
) => formatCharactersForUrlPathname(value, space, equalLength).toLowerCase();

// Counterpart to `sanitizePathname`, which percent-encodes `[`/`]` (next-intl's `getPathname` treats
// literal brackets as pathname params) and lowercases its result, so canonical slugs carry `%5b`/`%5d`.
// Pathnames reach the middleware URI-decoded, so encode them back before comparing them to a canonical slug.
export const encodePathnameBrackets = (value: string) =>
  value.replaceAll('[', '%5b').replaceAll(']', '%5d');

export type RawSearchParams = Record<string, string>;

// remove 'undefined' values from an query object
export const sanitizeQuery = <T extends Record<string, unknown> | undefined>(
  obj?: T
): Partial<T> | undefined => {
  if (!obj) {
    return undefined;
  }

  const params = Object.entries(obj).filter(([, value]) => {
    if (value === undefined) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'number' && isNaN(value)) {
      return false;
    }

    return value !== 'undefined';
  });

  if (!params.length) {
    return undefined;
  }

  return Object.fromEntries(params) as Partial<T>;
};

type CYOListPageProps = {
  departmentSlug?: string;
  categorySlugs?: string[];
  queryParams?: AssortmentSearchParams & UnknownSearchParams;
};

export const getCYOListPageSlug = ({
  departmentSlug,
  categorySlugs,
}: Pick<CYOListPageProps, 'departmentSlug' | 'categorySlugs'>): string[] => {
  return [...(departmentSlug ? [departmentSlug] : []), ...(categorySlugs || [])];
};

export const getCYOListPageHrefArgs = ({
  departmentSlug,
  categorySlugs,
  queryParams,
}: CYOListPageProps = {}): GetPathNameArgs['href'] => ({
  pathname: '/cyo/assortment/list/[[...slug]]',
  params: {
    slug: getCYOListPageSlug({ departmentSlug, categorySlugs }),
  },
  query: sanitizeQuery(queryParams),
});

export const getCYODesignerPageHrefArgs = (
  queryParams: CyoDesignerSearchParams
): GetPathNameArgs['href'] => ({
  pathname: '/cyo/designer',
  query: sanitizeQuery(queryParams),
});

type GetMPListPageProps = {
  departmentSlug?: string;
  categorySlugs?: string[];
  /**
   * Managed topic / suggested search term → `/shop/.../s/<slug>`.
   * Do not use for free-text Enter; pass `userQuery` instead.
   */
  searchTerm?: string;
  /**
   * Free-text user search → `?query=<term>` (no `/s/` segment). SEO-noindex path.
   * Ignored when `searchTerm` is also set (managed topic wins).
   */
  userQuery?: string;
  queryParams?: MPSearchParams & UnknownSearchParams;
};

export const getMPListPageSlug = ({
  departmentSlug,
  categorySlugs,
  searchTerm,
}: Pick<GetMPListPageProps, 'departmentSlug' | 'categorySlugs' | 'searchTerm'>): string[] => {
  const searchSlug = searchTerm ? slugifySearchTerm(searchTerm) : '';

  return [
    ...(departmentSlug ? [departmentSlug] : []),
    ...(categorySlugs || []),
    ...(searchSlug ? ['s', searchSlug] : []),
  ];
};

/**
 * Strips the search-term part (everything from the `s` marker onwards) off a marketplace list page
 * slug, leaving just the department/category slugs.
 */
export const getCategorySlug = (slug: string[] | undefined): string[] => {
  if (!slug) return [];
  const searchPrefixIndex = slug.indexOf('s');
  return searchPrefixIndex >= 0 ? slug.slice(0, searchPrefixIndex) : slug;
};

export const getMPListPageHrefArgs = ({
  departmentSlug,
  categorySlugs,
  searchTerm,
  userQuery,
  queryParams,
}: GetMPListPageProps = {}): GetPathNameArgs['href'] => {
  const { query: _ignoredQuery, ...restQueryParams } = queryParams ?? {};
  const resolvedUserQuery = searchTerm ? undefined : userQuery?.trim() || undefined;

  return {
    pathname: '/shop/[[...slug]]',
    params: {
      slug: getMPListPageSlug({ departmentSlug, categorySlugs, searchTerm }),
    },
    query: sanitizeQuery({
      ...restQueryParams,
      ...(resolvedUserQuery ? { query: resolvedUserQuery } : {}),
    }),
  };
};

type GetShowroomProps = GetMPListPageProps & { userSlug: string };

export const getShowroomSlug = ({
  userSlug,
  departmentSlug,
  categorySlugs,
}: Pick<GetShowroomProps, 'userSlug' | 'departmentSlug' | 'categorySlugs'>): string[] => {
  return [
    userSlug.replaceAll(' ', '+').toLowerCase(),
    ...(departmentSlug ? [departmentSlug] : []),
    ...(categorySlugs || []),
  ];
};

export const getShowroomHrefArgs = ({
  userSlug,
  departmentSlug,
  categorySlugs,
  queryParams,
}: GetShowroomProps): GetPathNameArgs['href'] => {
  return {
    pathname: '/shop/user/[[...slug]]',
    params: {
      slug: getShowroomSlug({ userSlug, departmentSlug, categorySlugs }),
    },
    query: sanitizeQuery(queryParams),
  };
};

export const getAssortmentDetailHrefArgs = ({
  productTypeId,
  productTypeName,
  queryParams,
}: {
  productTypeId: string;
  productTypeName: string;
  queryParams?: AssortmentDetailSearchParams & UnknownSearchParams;
}): GetPathNameArgs['href'] => ({
  pathname: '/cyo/assortment/detail/[slug]-PT[ptId]',
  params: { slug: sanitizePathname(productTypeName), ptId: productTypeId },
  query: sanitizeQuery(queryParams),
});

type ProductDetailPageHrefProps = {
  compositionId: string;
  sellableName: string;
  productTypeName: string;
  queryParams?: ProductDetailSearchParams & UnknownSearchParams;
};

export const getProductDetailPageSlug = ({
  sellableName,
  productTypeName,
}: Pick<ProductDetailPageHrefProps, 'productTypeName' | 'sellableName'>): string =>
  sanitizePathname(`${sellableName} ${productTypeName}`, '+');

export const getProductDetailPageHrefArgs = ({
  compositionId,
  sellableName,
  productTypeName,
  queryParams,
}: ProductDetailPageHrefProps): GetPathNameArgs['href'] => ({
  pathname: '/shop/design/[slug]-D[compositionId]',
  params: {
    slug: getProductDetailPageSlug({ sellableName, productTypeName }),
    compositionId: compositionId,
  },
  query: sanitizeQuery(queryParams),
});

/**
 * @deprecated use getProductDetailPageHrefArgs instead
 */
export const getSellableDetailHrefArgs = ({
  compositionId,
  sellableName,
  productTypeName,
  sellableId,
  appearanceId,
  sizeId,
  ...unknownSearchParams
}: {
  compositionId: string;
  sellableName: string;
  productTypeName: string;
  sellableId?: string;
  appearanceId?: string;
  sizeId?: string;
} & UnknownSearchParams): GetPathNameArgs['href'] =>
  getProductDetailPageHrefArgs({
    compositionId,
    sellableName,
    productTypeName,
    queryParams: {
      sellable: sellableId,
      appearance: appearanceId ? Number(appearanceId) : undefined,
      size: sizeId ? Number(sizeId) : undefined,
      ...unknownSearchParams,
    },
  });

export const getReturnGuaranteeLink = (locale: Locale) => {
  let lang: string = getLanguage(locale);
  const country = getCountry(locale);

  if (lang === 'en') {
    lang += country === 'US' || country === 'CA' ? '-us' : '-gb';
  } else if (country === 'CA') {
    lang += '-ca';
  }
  return 'https://help.spreadshirt.com/hc/' + lang + '/articles/207165559';
};

/**
 * Matches a pathname against a source pattern with wildcards ('*'), and replaces wildcards in the target pattern.
 * Returns the replaced target if matched, or null if not matched.
 */
// Example: matchAndReplaceWildcards('/foo/*/bar/*', '/fuu/*/baa/*', '/foo/abc/bar/123') // => '/fuu/abc/baa/123'
export function matchAndReplaceWildcards(
  source: string,
  target: string,
  pathname: string
): string | null {
  // Convert source to regex, escape special chars except '*', replace '*' with '(.+)' for greedy match
  const regexPattern =
    '^' +
    source
      .split('*')
      .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('(.+)') +
    '$';

  const regex = new RegExp(regexPattern);

  const match = pathname.match(regex);

  if (!match) return null;
  let replaced = target;
  for (let i = 1; i < match.length; i++) {
    replaced = replaced.replace('*', match[i] ?? '');
  }
  return replaced;
}

export function urlEncodePathname(path: string): string {
  return path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
}
